import type { Therapy } from "./types";

/**
 * Therapy explainers, part two. Original Recovery Trauma™ wording.
 * Psychoeducation only — nothing here is therapy, diagnosis or treatment.
 */
export const THERAPIES_PART_TWO: Therapy[] = [
  {
    slug: "schema-therapy",
    name: "Schema Therapy",
    badge: "Relational & Depth",
    group: "relational-depth",
    tagline:
      "A longer-term integrative therapy for lifelong patterns — the deep templates about self and others formed when core needs went unmet in childhood.",
    evidence: "broader",
    evidenceSummary:
      "Strong evidence for personality difficulties, particularly borderline personality disorder; trauma-specific PTSD trials are limited but growing.",
    whatIsIt: [
      "Schema therapy blends cognitive, behavioural, attachment, gestalt and psychodynamic ideas into one model of long-standing patterns.",
      "A 'schema' is an early template — abandonment, defectiveness, mistrust/abuse, emotional deprivation — that keeps repeating in adult life.",
      "'Modes' are the states you flip into: the vulnerable child, the angry child, the punitive inner critic, the detached protector, the healthy adult.",
    ],
    howItWorks: [
      "The therapist helps identify which schemas and modes are active and how they were formed.",
      "Limited reparenting — a warm, boundaried therapeutic relationship — provides some of the emotional experience that was missing.",
      "Experiential techniques bring the pattern into the room so it can be worked with emotionally, not just understood intellectually.",
    ],
    theory: [
      "The model proposes five core emotional needs in childhood: secure attachment, autonomy, freedom to express needs and feelings, spontaneity and play, and realistic limits.",
      "When those needs go unmet — especially with abuse, neglect or instability — maladaptive schemas form and are then maintained by surrender, avoidance or overcompensation.",
      "Change comes from meeting the unmet need in an adult way, not only from disputing the belief.",
    ],
    session: [
      "Sessions are typically weekly, 50–60 minutes, over a longer course than most CBT.",
      "Early sessions involve assessment, questionnaires and mapping your schemas and modes.",
      "Later sessions include imagery work, chair dialogues and relational exploration, alongside practical behaviour change.",
    ],
    techniques: [
      "Schema and mode assessment questionnaires",
      "Imagery rescripting of childhood scenes",
      "Chair work between modes (e.g. critic and vulnerable child)",
      "Limited reparenting within the therapeutic relationship",
      "Empathic confrontation",
      "Behavioural pattern-breaking assignments",
    ],
    usedFor: [
      "Long-standing relationship and self-worth patterns",
      "Borderline and other personality difficulties",
      "Chronic depression and anxiety that hasn't responded to shorter therapy",
      "Childhood emotional neglect and abuse histories",
    ],
    trauma: [
      "Imagery rescripting has become one of the more promising trauma techniques within schema therapy, allowing a childhood scene to be revisited and responded to differently.",
      "Schema therapy is generally less about single-event PTSD and more about who the trauma made you become.",
    ],
    complexTrauma: [
      "Schema therapy is a natural fit for C-PTSD: it was built for developmental, relational and repeated harm rather than one-off events.",
      "The inner critic and detached-protector modes map closely onto complex trauma experiences of shame and numbing.",
      "It is usually long-term work, which needs to be affordable and sustainable for you before starting.",
    ],
    duration: [
      "Commonly 1–3 years, sometimes longer for personality-level work.",
      "Shorter group and time-limited formats exist (around 20–40 sessions).",
    ],
    experience: [
      "Imagery and chair work can be emotionally intense and often surface grief for what was missing.",
      "People frequently describe the therapy relationship itself as the part that changed things.",
      "Progress tends to be gradual, with pattern recognition arriving before pattern change.",
    ],
    benefits: [
      "Addresses lifelong patterns rather than current symptoms only",
      "Explicitly relational, which suits attachment wounds",
      "Strong evidence in personality difficulties",
      "Integrates thinking, feeling, body and behaviour",
    ],
    limitations: [
      "Long and often expensive",
      "Not a first-line treatment for single-incident PTSD",
      "Emotionally demanding; needs a stable enough life situation",
      "Availability of properly certified therapists is limited in some areas",
    ],
    evidenceBase: [
      "Randomised trials support schema therapy for borderline personality disorder, including trials showing benefit over treatment as usual.",
      "Evidence for chronic depression, anxiety and other personality difficulties is moderate and growing.",
      "Imagery rescripting has its own encouraging trauma evidence, including in PTSD, though trials remain relatively small.",
      "It is not currently a guideline-recommended first-line PTSD treatment.",
    ],
    whoProvides: [
      "Registered psychologists, psychotherapists or CBT therapists with schema therapy training.",
      "The International Society of Schema Therapy (ISST) certifies practitioners at standard and advanced levels.",
    ],
    selfHelp: [
      "Self-help books and schema questionnaires can help you recognise your patterns, and that recognition is often valuable in itself.",
      "Imagery rescripting of traumatic childhood scenes should be done with a trained therapist, not alone.",
    ],
    questions: [
      "Are you ISST-certified, and at what level?",
      "How long do you expect this work might take, and how will we review it?",
      "How do you handle imagery work if I dissociate?",
      "What does limited reparenting mean in practice, and what are the boundaries?",
    ],
    resources: [
      { label: "Inner Child Journal", to: "/inner-child", blurb: "A private space for the younger parts of your story." },
      { label: "Shadow Work Journey", to: "/shadow-work", blurb: "Meet the patterns you'd rather not look at, gently." },
      { label: "Shame-based trauma", to: "/learn/shame-based-trauma", blurb: "Where the inner critic came from." },
    ],
    references: [
      {
        label: "International Society of Schema Therapy",
        source: "ISST",
        url: "https://schematherapysociety.org/",
      },
      {
        label: "Schema therapy for borderline personality disorder: trial evidence",
        source: "PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=schema+therapy+borderline+randomized",
      },
      {
        label: "Borderline personality disorder guideline CG78",
        source: "NICE (UK)",
        url: "https://www.nice.org.uk/guidance/cg78",
      },
    ],
  },

  {
    slug: "narrative-exposure-therapy",
    name: "Narrative Exposure Therapy",
    abbrev: "NET",
    badge: "Trauma Processing",
    group: "trauma-processing",
    tagline:
      "A short, structured therapy for people with multiple traumatic events, building a chronological life narrative so each memory finds its place in time.",
    evidence: "established",
    evidenceSummary:
      "Guideline-recognised for PTSD after multiple and organised violence, with randomised trials in refugee, conflict-affected and survivor populations.",
    whatIsIt: [
      "NET was developed for survivors of war, torture, displacement and repeated violence — situations where 'pick one index trauma' makes no sense.",
      "Across sessions you construct a chronological narrative of your whole life, not just the worst events.",
      "The narrative is written down by the therapist and given to you at the end as a signed document — a testimony.",
    ],
    howItWorks: [
      "A lifeline is laid out physically, often with flowers for positive events and stones for traumatic ones.",
      "Working chronologically, each stone is revisited in detail, with emotions and body sensations included, and then placed clearly in the past.",
      "Linking each memory to its time and place is intended to counter the timeless, always-now quality of traumatic memory.",
    ],
    theory: [
      "NET draws on the distinction between 'hot memory' (sensory, emotional, context-free) and 'cold memory' (autobiographical, located in time).",
      "PTSD is understood as a fear network of hot memories disconnected from context; narration reconnects them.",
      "The testimony element also carries a human-rights dimension: what happened is recorded and acknowledged.",
    ],
    session: [
      "Usually 90-minute weekly sessions, delivered as a defined short course.",
      "The first session builds the lifeline; subsequent sessions work through it in order.",
      "Each session begins by reading back the narrative so far, which is corrected and extended.",
      "The final session hands over the completed written testimony.",
    ],
    techniques: [
      "Lifeline exercise with symbolic objects",
      "Chronological narration of the full life story",
      "Detailed sensory and emotional recounting of traumatic events",
      "Explicit contextualisation in time and place",
      "Written testimony read back and handed over",
    ],
    usedFor: [
      "PTSD following multiple traumatic events",
      "Refugees, asylum seekers and survivors of conflict, torture and organised violence",
      "Survivors of prolonged domestic or community violence",
      "A child-adapted version (KIDNET) exists",
    ],
    trauma: [
      "NET is one of the few structured PTSD treatments designed from the start for multiple, cumulative trauma.",
      "It can be delivered in low-resource settings and by trained non-specialists under supervision, which matters where specialists are scarce.",
    ],
    complexTrauma: [
      "The chronological, whole-life structure suits complex trauma, because it includes the good and neutral parts of life as well as the harm.",
      "Where dissociation is severe, additional stabilisation is normally added before or alongside.",
      "It is emotionally demanding, and needs a stable enough current situation — though it has been used successfully with people whose circumstances remain uncertain.",
    ],
    duration: [
      "Typically 8–12 sessions, sometimes as few as 4 in brief formats.",
      "Longer courses are used where there are very many events.",
    ],
    experience: [
      "Recounting events in detail is distressing in the moment; the therapist stays with you and the session doesn't end in the middle of a hot memory.",
      "Many people describe the finished testimony as significant — proof, in their own words, that it happened.",
      "Symptom relief often becomes noticeable in the second half of the course.",
    ],
    benefits: [
      "Designed for multiple traumas rather than one",
      "Short, structured and transportable across cultures and settings",
      "Produces a tangible document with personal and sometimes legal value",
      "Good evidence in refugee and conflict-affected populations",
    ],
    limitations: [
      "Detailed recounting is demanding and not everyone wants it",
      "Requires a therapist trained in NET",
      "Written testimony raises confidentiality and safety questions that must be planned for",
      "Less commonly available than CBT or EMDR in general services",
    ],
    evidenceBase: [
      "Multiple randomised controlled trials support NET for PTSD in refugees, asylum seekers and survivors of organised violence, including in low-resource settings.",
      "NICE lists NET among trauma-focused psychological interventions considered for PTSD; ISSTS guidance also recognises it.",
      "Meta-analyses report moderate to large effects, with some heterogeneity across trials.",
    ],
    whoProvides: [
      "Registered mental health professionals trained in NET, often through vivo international or affiliated trainers.",
      "In humanitarian settings, trained lay counsellors deliver NET under structured clinical supervision.",
    ],
    selfHelp: [
      "Writing a broad life timeline — periods, moves, relationships, turning points — can be done safely alone and is often clarifying.",
      "Detailed narration of traumatic events is the part that needs a trained therapist present.",
    ],
    questions: [
      "What is your NET training, and how many courses have you delivered?",
      "How will my testimony be stored, and who will have access to it?",
      "Could the document be used in an asylum or legal process, and should it be?",
      "How do we handle it if I can't finish an event within a session?",
    ],
    resources: [
      { label: "Music, Memory & Healing Journal", to: "/music-journal", blurb: "A gentler way to build a life timeline." },
      { label: "Abuse Recovery & Evidence Journal", to: "/abuse-journal", blurb: "A private, dated record in your own words." },
      { label: "Grounding toolkit", to: "/grounding", blurb: "Come back to now after difficult memory work." },
    ],
    references: [
      { label: "PTSD guideline NG116", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng116" },
      {
        label: "Narrative Exposure Therapy trial evidence",
        source: "PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=narrative+exposure+therapy+randomized+controlled+trial",
      },
      {
        label: "ISTSS prevention and treatment guidelines",
        source: "International Society for Traumatic Stress Studies",
        url: "https://istss.org/clinical-resources/treating-trauma/new-istss-prevention-and-treatment-guidelines",
      },
    ],
  },

  {
    slug: "compassion-focused-therapy",
    name: "Compassion-Focused Therapy",
    abbrev: "CFT",
    badge: "Compassion & Shame",
    group: "parts-compassion",
    tagline:
      "Developed for people whose self-criticism and shame are so strong that ordinary therapy 'makes sense but doesn't feel true'. CFT builds the capacity to be warm towards yourself.",
    evidence: "broader",
    evidenceSummary:
      "Growing evidence for shame, self-criticism, depression and eating difficulties; trauma-specific randomised trials remain limited.",
    whatIsIt: [
      "CFT was developed by Paul Gilbert for high shame and self-attack, where insight alone doesn't change how someone feels.",
      "It trains the capacity for compassion — towards yourself, from others, and towards others — as a deliberate skill.",
      "Compassion here means the courage to turn towards suffering and act to ease it, not softness or letting things go.",
    ],
    howItWorks: [
      "Psychoeducation about the 'tricky brain' reduces shame: much of what you struggle with is old design, not personal failure.",
      "Three emotion-regulation systems are mapped — threat, drive and soothing — and most people with trauma find the soothing system underdeveloped.",
      "Practices such as soothing rhythm breathing, compassionate imagery and compassionate-self work strengthen that system over time.",
    ],
    theory: [
      "CFT draws on evolutionary psychology, attachment theory and affective neuroscience.",
      "The soothing/affiliative system develops through experiences of care; where care was absent or dangerous, warmth can itself feel threatening.",
      "That 'fear of compassion' is expected and worked with directly rather than treated as resistance.",
    ],
    session: [
      "Sessions are usually weekly, 50–60 minutes, mixing conversation with guided practice.",
      "You'll often be led through short imagery or breathing exercises in session and asked to try them between sessions.",
      "Chair work and compassionate letter writing are common.",
    ],
    techniques: [
      "Soothing rhythm breathing",
      "Compassionate imagery and safe-place work",
      "Compassionate-self and compassionate-other exercises",
      "Compassionate letter writing",
      "Multiple-selves chair work (angry self, anxious self, sad self)",
      "Working explicitly with fear and blocks to compassion",
    ],
    usedFor: [
      "Chronic shame and severe self-criticism",
      "Depression, social anxiety and eating difficulties",
      "Trauma-related self-blame",
      "People who find standard CBT intellectually convincing but emotionally hollow",
    ],
    trauma: [
      "Shame is a central feature of much trauma, especially interpersonal trauma, and CFT targets it directly.",
      "It is frequently used as a preparation or companion to trauma-processing therapy rather than instead of it.",
    ],
    complexTrauma: [
      "CFT is well suited to complex trauma, where the harshest voice is often internal and constant.",
      "Warmth can initially provoke grief, anger or fear — a known effect that a trained therapist expects and paces for.",
      "It pairs naturally with parts-based work such as IFS.",
    ],
    duration: [
      "Commonly 12–20 sessions, though longer courses are used for entrenched shame.",
      "Group CFT programmes are widely delivered in around 8–12 weeks.",
    ],
    experience: [
      "Many people find compassion practices unexpectedly hard, and tears in early exercises are common.",
      "Change often appears first as a slightly softer internal tone rather than a dramatic shift.",
      "The psychoeducation alone frequently reduces shame in the first sessions.",
    ],
    benefits: [
      "Directly targets shame and self-criticism, which many therapies leave untouched",
      "Practices are learnable and portable",
      "Reduces the 'I know it but I don't feel it' problem",
      "Combines well with other therapies",
    ],
    limitations: [
      "Not a trauma memory-processing treatment",
      "Compassion work can initially increase distress",
      "PTSD-specific trial evidence is limited",
      "Quality depends heavily on the therapist's own training in the model",
    ],
    evidenceBase: [
      "Systematic reviews report benefits of CFT for self-criticism, shame, depression and eating difficulties, with effects generally moderate.",
      "Trial quality is mixed and many studies are small, uncontrolled or short-term.",
      "There is early evidence for CFT in PTSD populations, but it is not a guideline-recommended first-line trauma treatment.",
    ],
    whoProvides: [
      "Registered psychologists, psychotherapists and counsellors with CFT training.",
      "The Compassionate Mind Foundation provides training and practitioner information.",
    ],
    selfHelp: [
      "CFT translates well to self-help: soothing rhythm breathing, compassionate letter writing and self-compassion exercises are safe to practise alone.",
      "If warmth towards yourself brings a strong wave of grief or fear, that's a sign to slow down and ideally do this work with support.",
    ],
    questions: [
      "What CFT training do you have?",
      "How do you work with people who find compassion frightening?",
      "Would you combine this with trauma-focused therapy, and when?",
      "What practices would I be doing between sessions?",
    ],
    resources: [
      { label: "Shame-based trauma", to: "/learn/shame-based-trauma", blurb: "Why shame sticks, and what softens it." },
      { label: "Inner Child Journal", to: "/inner-child", blurb: "Practise a warmer inner tone in writing." },
      { label: "Breathing tools", to: "/breathing", blurb: "Soothing rhythm breathing you can use today." },
    ],
    references: [
      {
        label: "Compassionate Mind Foundation",
        source: "Compassionate Mind Foundation",
        url: "https://www.compassionatemind.co.uk/",
      },
      {
        label: "Compassion-focused therapy: systematic review evidence",
        source: "PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=compassion+focused+therapy+systematic+review",
      },
      { label: "Depression in adults NG222", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng222" },
    ],
  },

  {
    slug: "psychodynamic-therapy",
    name: "Psychodynamic Therapy",
    badge: "Relational & Depth",
    group: "relational-depth",
    tagline:
      "An exploratory therapy focused on how earlier relationships and out-of-awareness patterns shape present feeling, behaviour and relationships.",
    evidence: "broader",
    evidenceSummary:
      "A well-established psychotherapy with meta-analytic support for depression and anxiety; trauma-focused versions exist but PTSD-specific trial evidence is more limited.",
    whatIsIt: [
      "Psychodynamic therapy is a talking therapy that looks beneath the presenting problem to recurring patterns and their origins.",
      "It emphasises the relationship with the therapist as a live sample of how you relate more generally.",
      "It can be brief and focused (16–30 sessions) or open-ended and long-term.",
    ],
    howItWorks: [
      "You speak relatively freely; the therapist listens for patterns, gaps, defences and repetitions.",
      "Feelings that arise towards the therapist (transference) are treated as useful information rather than a distraction.",
      "Understanding a pattern in the room, as it happens, is what gives it a chance to change.",
    ],
    theory: [
      "It developed from psychoanalysis but modern practice is shaped by attachment theory, object relations and relational psychoanalysis.",
      "Defences — dissociation, denial, intellectualising — are understood as once-necessary protections, not flaws.",
      "Symptoms are read as meaningful communications rather than only as problems to remove.",
    ],
    session: [
      "Typically 50 minutes, weekly (sometimes more frequent), often at a consistent time to establish reliability.",
      "There is usually no agenda or homework; the session begins where you begin.",
      "Silence, and attention to what's hard to say, are part of the method rather than dead time.",
    ],
    techniques: [
      "Free-form exploration and following associations",
      "Attention to transference and countertransference",
      "Interpretation and linking present to past",
      "Working with defences and resistance",
      "Dream and fantasy material, in some traditions",
      "Consistent frame — time, fee, boundaries — as a therapeutic tool",
    ],
    usedFor: [
      "Depression, anxiety and long-standing relationship difficulties",
      "Loss, grief and identity questions",
      "Personality difficulties and recurring life patterns",
      "People wanting to understand themselves, not only reduce symptoms",
    ],
    trauma: [
      "Trauma-focused psychodynamic approaches address the meaning of the trauma, the impact on identity, and the relational rupture it caused.",
      "The reliable relationship itself can be reparative where trauma was interpersonal.",
      "For core PTSD symptoms, trauma-focused CBT, EMDR, CPT or PE have substantially stronger trial evidence.",
    ],
    complexTrauma: [
      "Long-term psychodynamic work is commonly used for complex trauma, where attachment and identity are central.",
      "Unstructured exploration can be destabilising without enough grounding, so trauma-informed practitioners build in stabilisation.",
      "Length and cost are real considerations; be clear about what you can sustain.",
    ],
    duration: [
      "Brief dynamic therapy: around 16–30 sessions.",
      "Long-term work: one to several years, sometimes more than once a week.",
    ],
    experience: [
      "The openness can feel disorientating at first, especially after structured therapy.",
      "Strong feelings towards the therapist are normal and are meant to be spoken about.",
      "Insight often arrives gradually and unevenly rather than on a schedule.",
    ],
    benefits: [
      "Depth of understanding beyond symptom management",
      "Well suited to relational and identity difficulties",
      "Meta-analyses suggest gains often continue after therapy ends",
      "Flexible, not manualised",
    ],
    limitations: [
      "Slower and often more expensive",
      "Less structure and fewer measurable milestones",
      "Weaker PTSD-specific evidence than front-line trauma treatments",
      "Fit with the individual therapist matters enormously",
    ],
    evidenceBase: [
      "Meta-analyses support short-term psychodynamic psychotherapy for depression, anxiety and somatic conditions, with effects broadly comparable to other active therapies.",
      "Some research suggests gains persist or increase after treatment ends, though this finding is debated.",
      "PTSD-specific randomised evidence is limited; NICE guidance positions trauma-focused therapies first for PTSD.",
    ],
    whoProvides: [
      "Psychodynamic or psychoanalytic psychotherapists, counselling and clinical psychologists, and psychiatrists with analytic training.",
      "In the UK, check UKCP, BPC, BACP or HCPC registration.",
    ],
    selfHelp: [
      "Reflective journalling about recurring patterns, relationships and reactions is a genuinely useful parallel practice.",
      "The core mechanism here is relational, so it can't really be replicated alone.",
    ],
    questions: [
      "How do you work with trauma specifically within a psychodynamic frame?",
      "How long might this take, and how will we review whether it's helping?",
      "What happens if I feel worse for a while?",
      "What's your training and registration?",
    ],
    resources: [
      { label: "Recovery Journal", to: "/journal", blurb: "Notice the patterns between sessions." },
      { label: "Learn Hub: IFS", to: "/learn/what-is-ifs", blurb: "Another way of understanding inner conflict." },
      { label: "Shadow Work Journey", to: "/shadow-work", blurb: "Explore what usually stays out of view." },
    ],
    references: [
      {
        label: "Short-term psychodynamic psychotherapy: meta-analytic evidence",
        source: "PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=short-term+psychodynamic+psychotherapy+meta-analysis",
      },
      { label: "PTSD guideline NG116", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng116" },
      {
        label: "British Psychoanalytic Council",
        source: "BPC",
        url: "https://www.bpc.org.uk/",
      },
    ],
  },

  {
    slug: "interpersonal-therapy",
    name: "Interpersonal Therapy",
    abbrev: "IPT",
    badge: "Relational & Depth",
    group: "relational-depth",
    tagline:
      "A time-limited therapy that treats distress by working on what's happening in your relationships now — grief, disputes, life transitions and isolation.",
    evidence: "broader",
    evidenceSummary:
      "Strong, guideline-backed evidence for depression; a growing but smaller evidence base for PTSD, where it is notable for not requiring exposure.",
    whatIsIt: [
      "IPT is a structured, time-limited therapy, usually 12–16 sessions, focused on current relationships and social functioning.",
      "It doesn't assume relationships caused the problem — only that distress and relationships affect each other, and that the relational side is workable.",
      "Work is organised around one or two focus areas agreed at the start.",
    ],
    howItWorks: [
      "The opening phase maps your relationships (an interpersonal inventory) and selects a focus: grief, role disputes, role transitions, or interpersonal deficits/isolation.",
      "The middle phase works on that focus with communication analysis, role play and problem-solving.",
      "The final phase consolidates gains, plans for setbacks and addresses the ending itself.",
    ],
    theory: [
      "IPT grew from attachment and interpersonal theory: humans regulate through connection, and disrupted connection reliably affects mood.",
      "Symptoms are framed as a medical condition you're not to blame for, which lowers shame and frees energy for change.",
      "Improving communication and support tends to improve mood, without needing to analyse childhood in depth.",
    ],
    session: [
      "Weekly 50-minute sessions across a defined course.",
      "Each session starts by reviewing symptoms and the past week's interpersonal events.",
      "Specific interactions are examined closely — what was said, what was meant, what could be said differently.",
    ],
    techniques: [
      "Interpersonal inventory",
      "Communication analysis of specific conversations",
      "Role play and rehearsal",
      "Decision analysis and problem-solving",
      "Grief work for losses",
      "Explicit work on the ending of therapy",
    ],
    usedFor: [
      "Depression, including perinatal and adolescent depression",
      "Grief and complicated bereavement",
      "Life transitions — separation, migration, illness, redundancy",
      "Eating difficulties and some anxiety presentations",
    ],
    trauma: [
      "An IPT protocol for PTSD exists and has been tested in randomised trials, with results in some studies comparable to prolonged exposure.",
      "Its distinguishing feature is that it does not require recounting or reliving the trauma, which can suit people who won't or can't do exposure.",
      "It targets the relational aftermath of trauma — withdrawal, mistrust, numbness towards others.",
    ],
    complexTrauma: [
      "IPT can help rebuild support and connection, which is often badly damaged by complex trauma.",
      "It is not designed to process traumatic memory, so it is often used alongside or before trauma-focused work.",
      "The time-limited frame may feel too short for long-standing developmental trauma.",
    ],
    duration: [
      "Typically 12–16 weekly sessions.",
      "Maintenance sessions (monthly) are sometimes added for recurrent depression.",
    ],
    experience: [
      "People often notice mood shifting as concrete relationship situations improve.",
      "The focus on the here and now feels practical to some and too surface-level to others.",
      "The planned ending is worked with directly, which many find unusually containing.",
    ],
    benefits: [
      "Strong evidence in depression and recommended in major guidelines",
      "Time-limited, clear and collaborative",
      "No requirement to relive traumatic events",
      "Improves support networks, which protects against relapse",
    ],
    limitations: [
      "Not a trauma memory-processing treatment",
      "PTSD evidence base is smaller than for EMDR, CPT, PE or TF-CBT",
      "The short frame may not suit complex, long-standing difficulties",
      "Less useful if there's very little current social contact to work with",
    ],
    evidenceBase: [
      "IPT has extensive randomised trial support for depression and is recommended in NICE depression guidance (NG222).",
      "A randomised non-inferiority trial of IPT for PTSD reported outcomes comparable to prolonged exposure, particularly for people with co-occurring depression.",
      "Overall the PTSD evidence base remains smaller, so IPT is generally considered an alternative rather than a first-line trauma treatment.",
    ],
    whoProvides: [
      "Registered psychological therapists trained in IPT and supervised in the model.",
      "IPT-UK and the International Society of Interpersonal Psychotherapy accredit practitioners.",
    ],
    selfHelp: [
      "Mapping your own relationships and noticing which ones drain or restore you is a useful exercise on your own.",
      "Communication analysis is easier with a therapist, but journalling one difficult conversation a week works surprisingly well as a starting point.",
    ],
    questions: [
      "Are you IPT-accredited, and have you delivered the PTSD adaptation?",
      "Which focus area would you suggest for me, and why?",
      "What if trauma memories come up during the work?",
      "What happens at the end of the 16 sessions if I still need support?",
    ],
    resources: [
      { label: "Recovery Journal", to: "/journal", blurb: "Track relationships, conversations and mood." },
      { label: "Community", to: "/community", blurb: "Connection with people who understand." },
      { label: "Learn Hub: DBT", to: "/learn/dbt", blurb: "Interpersonal effectiveness skills." },
    ],
    references: [
      { label: "Depression in adults NG222", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng222" },
      {
        label: "Interpersonal psychotherapy for PTSD randomised trial",
        source: "PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=interpersonal+psychotherapy+PTSD+randomized",
      },
      {
        label: "International Society of Interpersonal Psychotherapy",
        source: "ISIPT",
        url: "https://interpersonalpsychotherapy.org/",
      },
    ],
  },
];
